import { action, computed, observable, reaction, runInAction } from "mobx"

import { ProjectCoverModel } from "@/components/ProjectCoverEditor/ProjectCoverModel.ts"
import { Model } from "@/lib/Model.ts"
import { projectQueryKey, projectsV2QueryKey } from "@/lib/queries.ts"

import type { PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { ProjectPermissionsProp } from "@/routes/projects/projectHooks.ts"


export class ProjectGeneralTabModel extends Model {
  readonly coverModel: ProjectCoverModel

  readonly name = observable.box<string>("")
  readonly savedName = observable.box<string>("")
  readonly isSaving = observable.box<boolean>(false)

  constructor(
    readonly context: OrgContext,
    readonly project: PR_Project2,
    readonly permissions: ProjectPermissionsProp
  ) {
    super("ProjectGeneralTabModel")

    this.coverModel = new ProjectCoverModel(context, project.key, project.colorKey)

    this.name.set(project.name)
    this.savedName.set(project.name)

    this.lifetime.add(
      reaction(
        () => this.project.name,
        (projectName) => {
          this.name.set(projectName)
          this.savedName.set(projectName)
        }
      ),
      "ProjectGeneralTabModel:syncProjectName"
    )
  }

  hasChanged = computed(() => {
    return this.name.get().trim() !== this.savedName.get().trim()
  })

  canSave = computed(() => {
    return this.permissions.canEditProject.get() && this.hasChanged.get() && this.name.get().trim().length > 0
  })

  setName = action((newName: string) => {
    this.name.set(newName)
  })

  handleSave = action(async () => {
    if (this.isSaving.get()) return

    this.isSaving.set(true)
    try {
      const trimmedName = this.name.get().trim()
      if (trimmedName.length === 0) return
      await this.context.queries.apiClient.updateProjectName(this.project.key, trimmedName)
      void this.context.queryClient.invalidateQueries({ queryKey: projectsV2QueryKey })
      void this.context.queryClient.invalidateQueries(projectQueryKey(this.project.key))
      runInAction(() => this.savedName.set(trimmedName))
    } finally {
      runInAction(() => this.isSaving.set(false))
    }
  })

  handleCancel = action(() => {
    this.name.set(this.savedName.get())
  })

  terminate() {
    super.terminate()
    this.coverModel.terminate()
  }
}